// authUtils.js - Utilities for authentication and credential management
import { getRefreshableCredentials, isAwsAuthConfigured } from './awsAuth';

/**
 * Get authenticated credentials for AWS services
 * Handles both authenticated and unauthenticated access
 * @param {Object} options - Optional configuration 
 * @returns {Object} - Auth object with credentials and helper functions
 */
export function getAuth(options = {}) {
  try {
    // For React components, we should use the useAuth hook
    // But for non-component code, we need to provide a way to get credentials
    
    // Add tracking source to prevent infinite loops
    const trackingSource = options.trackingSource || 'generic';
    
    // Check if we're in a React component context by trying to access the auth context
    let authContext;
    try {
      authContext = window._authContextRef?.current;
    } catch (e) {
      // Not in a component context or reference not available
    }
    
    // If we have access to the auth context, use it
    if (authContext) {
      // First check if auth is ready for use
      if (!authContext.awsReady && !options.ignoreAwsReadyCheck) {
        console.warn('AWS authentication is not ready yet. Retrying may be necessary.');
      }
      
      return {
        getTemporaryCredentials: (opts = {}) => authContext.getTemporaryCredentials({ 
          ...opts, 
          trackingSource: `${trackingSource}-authContext` 
        }),
        refreshTokens: (opts = {}) => authContext.refreshTokens({ 
          ...opts, 
          source: `${trackingSource}-refresh` 
        }),
        isAuthenticated: authContext.isAuthenticated,
        awsReady: authContext.awsReady,
        getBedrockRuntimeClient: () => authContext.getBedrockRuntimeClient(),
        getDynamoDBClient: () => authContext.getDynamoDBClient(),
        getS3Client: () => authContext.getS3Client(),
        user: authContext.user
      };
    }
    
    // Fallback for non-component context
    // Check if AWS auth is configured properly
    if (!isAwsAuthConfigured()) {
      throw new Error('AWS authentication is not properly configured');
    }
    
    // Return an object with getTemporaryCredentials function
    return {
      getTemporaryCredentials: async (opts = {}) => {
        // Merge options with any passed options
        const mergedOptions = { 
          ...options, 
          ...opts,
          trackingSource: `${trackingSource}-fallback`
        };
        return await getRefreshableCredentials(mergedOptions);
      },
      // Implementation of S3Client for non-component context
      getS3Client: async () => {
        const { S3Client } = await import('@aws-sdk/client-s3');
        const credentials = await getRefreshableCredentials({
          ...options,
          trackingSource: `${trackingSource}-s3`
        });
        return new S3Client({
          region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
          credentials
        });
      },
      // Implementation of DynamoDBClient for non-component context
      getDynamoDBClient: async () => {
        const { DynamoDBClient } = await import('@aws-sdk/client-dynamodb');
        const credentials = await getRefreshableCredentials({
          ...options,
          trackingSource: `${trackingSource}-dynamo`
        });
        return new DynamoDBClient({
          region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
          credentials
        });
      },
      // Implementation of BedrockRuntimeClient for non-component context
      getBedrockRuntimeClient: async () => {
        const { BedrockRuntimeClient } = await import('@aws-sdk/client-bedrock-runtime');
        const credentials = await getRefreshableCredentials({
          ...options,
          trackingSource: `${trackingSource}-bedrock`
        });
        return new BedrockRuntimeClient({
          region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
          credentials
        });
      }
    };
  } catch (error) {
    console.error('Authentication error in getAuth:', error);
    throw error;
  }
}

/**
 * Verify if the current session is valid
 * @returns {Promise<boolean>} - Whether the session is valid
 */
export async function verifySession() {
  try {
    const auth = getAuth({ ignoreAwsReadyCheck: true });
    if (auth && auth.getTemporaryCredentials) {
      await auth.getTemporaryCredentials();
      return true;
    }
    return false;
  } catch (error) {
    console.log('Session verification failed:', error.message);
    return false;
  }
}

/**
 * Wait until AWS authentication is ready
 * Useful for components that need to use AWS services immediately
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns {Promise<boolean>} - Whether AWS is ready
 */
export async function waitForAwsReady(timeoutMs = 5000) {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeoutMs) {
    const auth = getAuth({ ignoreAwsReadyCheck: true });
    if (auth?.awsReady) {
      return true;
    }
    // Wait a bit before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  // Timeout reached
  return false;
}